/**
 * Astreintes (décision du propriétaire du 19 octobre 2026) : ce que la liste,
 * les pages de saisie, l'accueil et le Menu partagent sans rien dessiner —
 * données lues, regroupement par semaine, libellés, préremplissage. Testé
 * sans navigateur. L'astreinte est facultative : sans période, rien ne
 * change pour l'entreprise.
 */
import { useQuery } from '@tanstack/react-query';
import { ESCALATION_MAX_STEPS, type EscalationTarget } from '@myequip/shared';
import { api } from '../../core/api.js';

export const ONCALL_PATHS = {
  liste: '/equipe/astreintes',
  nouvelle: '/equipe/astreintes/nouvelle',
  roulement: '/equipe/astreintes/roulement',
  circuit: '/equipe/astreintes/circuit',
} as const;

export const oncallEditPath = (id: string): string => `${ONCALL_PATHS.liste}/${encodeURIComponent(id)}`;

/** Une période telle que la sert `GET /oncall`. */
export interface OncallPeriod {
  id: string;
  user_id: string;
  full_name: string;
  active_account: boolean;
  starts_at: string;
  ends_at: string;
  rank: number;
  sector: string | null;
  current: boolean;
  ended: boolean;
  /** Absence prévue de la personne sur la période (congé, indisponibilité, école). */
  absence: string | null;
  /** Un numéro de mobile est enregistré : sans lui, aucun SMS d'urgence ne peut lui parvenir. */
  has_mobile: boolean;
  can_edit: boolean;
}

export interface OncallPerson {
  id: string;
  user_id: string;
  full_name: string;
  phone: string | null;
  rank: number;
  sector: string | null;
  ends_at: string;
}

export interface OncallNow {
  inUse: boolean;
  active: OncallPerson[];
  mine: {
    current: { id: string; starts_at: string; ends_at: string; rank: number; sector: string | null } | null;
    next: { id: string; starts_at: string; ends_at: string; rank: number; sector: string | null } | null;
  };
}

export const ONCALL_KEYS = { now: ['oncall', 'now'], list: ['oncall', 'list'], circuit: ['oncall', 'circuit'] } as const;

/** Qui est d'astreinte maintenant, et sa propre astreinte : une lecture légère, partagée. */
export function useOncallNow(enabled = true) {
  return useQuery({
    queryKey: [...ONCALL_KEYS.now],
    queryFn: () => api<OncallNow>('/oncall/now'),
    staleTime: 60_000,
    enabled,
  });
}

export function rankLabel(rank: number): string {
  return rank <= 1 ? 'Principal' : 'Renfort';
}

const SHORT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const CLOCK = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const LONG_DAY = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
const LONG_DAY_YEAR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** « lun. 26 oct. 08:00 ». */
export function whenShort(iso: string | Date): string {
  const date = new Date(iso);
  return `${SHORT.format(date)} ${CLOCK.format(date)}`;
}

/** « lun. 26 oct. 08:00 → lun. 2 nov. 08:00 ». */
export function periodLabel(startsAt: string, endsAt: string): string {
  return `${whenShort(startsAt)} → ${whenShort(endsAt)}`;
}

/** Lundi (jour local) de la semaine d'un instant. */
export function mondayOf(iso: string | Date): Date {
  const date = new Date(iso);
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
}

function localDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export interface OncallWeek {
  key: string;
  label: string;
  periods: OncallPeriod[];
}

/**
 * Les périodes à venir par semaine de début (lundi local), dans l'ordre servi.
 * « Cette semaine », « Semaine prochaine », puis « Semaine du 9 novembre ».
 */
export function groupByWeek(periods: readonly OncallPeriod[], now = new Date()): OncallWeek[] {
  const thisWeek = mondayOf(now).getTime();
  const weeks = new Map<string, OncallWeek>();
  for (const period of periods) {
    // Une période en cours compte pour la semaine présente, même commencée avant.
    const monday = period.current ? new Date(thisWeek) : mondayOf(period.starts_at);
    const key = localDay(monday);
    let week = weeks.get(key);
    if (!week) {
      const offset = Math.round((monday.getTime() - thisWeek) / (7 * 86_400_000));
      const label = offset <= 0 ? 'Cette semaine'
        : offset === 1 ? 'Semaine prochaine'
          : `Semaine du ${(monday.getFullYear() === now.getFullYear() ? LONG_DAY : LONG_DAY_YEAR).format(monday)}`;
      week = { key, label, periods: [] };
      weeks.set(key, week);
    }
    week.periods.push(period);
  }
  return [...weeks.values()];
}

const ABSENCES: Record<string, string> = {
  conge: 'En congé sur cette période',
  indisponibilite: 'Indisponible sur cette période',
  ecole: 'À l’école sur cette période',
};

export function absenceWarning(kind: string | null | undefined): string | null {
  if (!kind) return null;
  return ABSENCES[kind] ?? 'Absent sur cette période';
}

/** Lundi prochain (jour local « AAAA-MM-JJ ») : le premier relais proposé d'un roulement. */
export function nextMonday(now = new Date()): string {
  const monday = mondayOf(now);
  monday.setDate(monday.getDate() + 7);
  return localDay(monday);
}

/** « 2026-10-26T08:00 » (heure locale) → ISO, ou chaîne vide si incomplet. */
export function localToIso(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/** ISO → « 2026-10-26T08:00 » en heure locale (champ date et heure). */
export function isoToLocal(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${localDay(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Ce qui manque à une période saisie, ou `null` si elle peut partir. */
export function periodProblem(input: { userId: string; startsAt: string; endsAt: string }, now = new Date()): string | null {
  if (!input.userId) return 'Choisissez la personne d’astreinte.';
  const start = localToIso(input.startsAt);
  const end = localToIso(input.endsAt);
  if (!start || !end) return 'Choisissez le début et la fin.';
  if (Date.parse(end) <= Date.parse(start)) return 'La fin doit être après le début.';
  if (Date.parse(end) - Date.parse(start) > (31 * 24 + 1) * 3_600_000) {
    return 'Une période dure au plus 31 jours : pour plus long, générez un roulement.';
  }
  if (Date.parse(end) <= now.getTime()) return 'Cette période est déjà terminée.';
  return null;
}

export const PERIOD_CHOICES = [
  { value: '7', label: '1 semaine' },
  { value: '1', label: '1 jour' },
  { value: '14', label: '2 semaines' },
] as const;

export const ESCALATION_LABELS: Record<string, string> = {
  astreinte: 'La personne d’astreinte',
  patron: 'Le patron',
  remplacant: 'Les renforts d’astreinte',
  utilisateur: 'Une personne choisie',
};

export interface EscalationStep { targetKind: EscalationTarget; targetUserId: string | null; delayMinutes: number }

const ESCALATION_TARGET_KINDS = new Set<string>(Object.keys(ESCALATION_LABELS));

/** Copie de secours : une liste d'étapes lisible, sinon rien n'est repris. */
export function parseEscalationSteps(raw: string): EscalationStep[] | null {
  try {
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list) || list.length > ESCALATION_MAX_STEPS) return null;
    const parsed: EscalationStep[] = [];
    for (const item of list as Array<Record<string, unknown> | null>) {
      if (!item || typeof item.targetKind !== 'string' || !ESCALATION_TARGET_KINDS.has(item.targetKind)
        || typeof item.delayMinutes !== 'number' || !(item.targetUserId === null || typeof item.targetUserId === 'string')) return null;
      parsed.push({ targetKind: item.targetKind as EscalationTarget, targetUserId: item.targetUserId, delayMinutes: item.delayMinutes });
    }
    return parsed;
  } catch {
    return null;
  }
}

